import PropTypes from "prop-types";
import { Fragment, cloneElement, isValidElement, useCallback, useEffect, useRef } from "react";
import moment from "moment";

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";

// API's
import { getPositionDraw } from "@/PayPlans/api/positions/queries";

// Contexts
import { usePayPlan } from "@PayPlans/context/PayPlans";

// Helpers
import { and, or } from "@/Common/helpers/sonar";

// Layouts
import EmptyBody from "@Layouts/components/Basics/EmptyBody";

// Images
import empty from "@/assets/images/commons/empty.png";

const isTimeToEdit = (updatedAt, minutes = 5) => moment().diff(moment(updatedAt), "minutes") <= minutes;

const getNewPayPlanDefinition = (initialModel = null, updatedSections = null, args = {}) => {
	const { location, user } = args;
	const { locationId = "" } = location || {};
	const { _id: userId = "" } = user || {};

	const payPlan = {
		...initialModel,
		...updatedSections,
		locationId,
		createdAt: new Date(),
		createdBy: userId,
		updatedAt: new Date(),
		updatedBy: userId,
	};

	// Remove unnecessary attributes
	delete payPlan._id;
	delete payPlan.locked;

	return payPlan;
};

const NEEDS_SANITIZATION = ["productBonuses"];
const isPropertyIncluded = (payPlan, property = "") => {
	if (!payPlan || !property) {
		return false;
	}
	return property in payPlan || {};
};

export default function FactoryModel(props) {
	const { children } = props;

	const location = useSelector((state) => state.location.location);
	const user = useSelector((state) => state.user.user);
	const queryClient = useQueryClient();

	const {
		emptySelectionTitle,
		instantCreation,
		initialModel,
		isNewPayPlan,
		isSomeNewPayPlan,
		position,
		positionId,
		services,
		selected,
		setIsLoadingModel,
		setIsPayPlanLocked,
		isPayPlanAvailable,
		setIsPayPlanAvailable,
		setInitialModel,
		setModel,
		setSelected,
		setSomeNewPayPlan,
		updatedSections,
		setIsEditing,
	} = usePayPlan();
	const { queries, mutations } = services;

	const isNew = isNewPayPlan();
	const wasCreated = useRef(false);
	const wasUpdated = useRef(false);

	// Queries
	const { data: drawModality, isLoading: isLoadingModality } = useQuery({
		queryKey: ["payPlan", "drawModality", positionId, location?.locationId, selected?.position],
		queryFn: async () => getPositionDraw(location?.locationId, selected?.position),
		staleTime: Infinity,
		gcTime: Infinity,
		enabled: and(!!location?.locationId, !!selected?.position),
	});

	const { data, isLoading, isFetching } = useQuery({
		queryKey: ["payPlan", positionId, location?.locationId, selected?._id],
		queryFn: async () => {
			const result = await queries?.getPayPlan(location?.locationId, selected?._id, selected?.position);

			// Sanitize special cases
			NEEDS_SANITIZATION.forEach((property) => {
				if (isPropertyIncluded(result, property)) {
					const isEmpty = !result?.[property]?.length;
					if (isEmpty) {
						result[property] = null;
					}
				}
			});

			return result || null;
		},
		staleTime: Infinity,
		gcTime: Infinity,
		enabled: and(!!location?.locationId, !!selected?._id),
	});

	const isEditingMode = (payPlan, userId) => {
		let editing = false;
		if (payPlan && userId && isTimeToEdit(payPlan?.updatedAt)) {
			editing = payPlan.updatedBy?._id !== userId;
		}
		return editing;
	};

	// Mutations
	const { mutate: updatePayPlan, isPending: updating } = useMutation({
		mutationKey: ["updatePayPlan", position, location?.locationId, selected?._id],
		mutationFn: (input) => mutations?.updatePayPlan({ ...input, position }),
		onSuccess: (response) => {
			const { createdAt, createdBy, updatedAt, updatedBy } = response || {};
			if (createdAt && createdBy && updatedAt && updatedBy) {
				queryClient.setQueryData(["payPlan", positionId, location?.locationId, selected?._id], (previous) => {
					previous["createdAt"] = createdAt;
					previous["createdBy"] = createdBy;
					previous["updatedAt"] = updatedAt;
					previous["updatedBy"] = updatedBy;
					return previous;
				});
			}
		},
	});

	const { mutate: savePayPlan, isPending: saving } = useMutation({
		mutationFn: (input) => mutations?.saveNewPayPlan({ ...input, position }),
	});

	const stateActions = and(isSomeNewPayPlan, !isNew) || or(isLoading, isLoadingModality);
	const isEditing = isEditingMode(data, user._id);
	const readOnly = or(isEditing, stateActions);

	// Callbacks / Handlers
	const update = useCallback(
		(updateData) => {
			setModel(updateData);
		},
		[setModel]
	);

	const updateSelectionTree = useCallback(
		(currentPositionId, payPlan) => {
			queryClient.setQueryData(["payPlansSidebarTree", "list", currentPositionId, payPlan?.locationId], (previous) => {
				if (previous) {
					const updateData = previous;
					const foundName = updateData?.inProgress?.some((element) => element.name === payPlan.name);
					if (foundName) {
						// If pay plan exist change the element identifier by the new one
						updateData.inProgress.forEach((element, key) => {
							if (Boolean(element.name === payPlan.name) && element._id?.includes("new_")) {
								updateData.inProgress[key]["_id"] = payPlan._id;
							}
						});
					} else {
						// Add to the tree if not in the list
						updateData.inProgress.push({
							_id: payPlan._id,
							name: payPlan.name,
							position: payPlan.position,
						});
					}
					return updateData;
				}

				return previous;
			});
		},
		[queryClient]
	);

	const cacheNewPayPlan = useCallback(
		(previousId, payPlan) => {
			queryClient.removeQueries(["payPlan", positionId, payPlan?.locationId, previousId], { exact: true });
			queryClient.setQueryData(["payPlan", positionId, payPlan?.locationId, payPlan._id], payPlan);
		},
		[queryClient, positionId]
	);

	const handleUpdatePayPlan = useCallback(
		(updated) => {
			const { locationId } = location || {};
			const payPlanId = selected?._id;
			const updatedBy = user?._id;
			const updatedPayPlan = { ...updated, updatedBy, _id: payPlanId };

			updatePayPlan(
				{
					locationId,
					payPlan: updatedPayPlan,
				},
				{
					onSuccess: (payPlan) => {
						// Refreshing matches
						queryClient.invalidateQueries(["payPlan", "matches"]);
						// Changing initial model after updating
						setInitialModel(payPlan);
						wasUpdated.current = false;
					},
					onError: (error) => {
						setInitialModel(initialModel);
						console.error(error);
					},
				}
			);
		},
		[location, selected, user, initialModel, setInitialModel]
	);

	const saveNewCreatedPayPlan = useCallback(
		(previousId, payPlan) => {
			if (payPlan) {
				// Refreshing matches
				queryClient.invalidateQueries(["payPlan", "matches"]);

				// Local cache (state) treatment
				cacheNewPayPlan(previousId, payPlan);

				// Selection
				updateSelectionTree(positionId, payPlan);
				setSelected({
					_id: payPlan._id,
					name: payPlan.name,
					position: payPlan.position,
				});
				setInitialModel(payPlan);
				setSomeNewPayPlan(false);

				// Setting editing mode
				setIsEditing(false);
				wasCreated.current = false;
			}
		},
		[positionId, setInitialModel, setSelected, setSomeNewPayPlan, setIsEditing, cacheNewPayPlan, updateSelectionTree]
	);

	const handleSaveNewPayPlan = useCallback(
		(createdPayPlan) => {
			const { locationId } = location || {};
			const currentId = initialModel?._id;

			savePayPlan(
				{
					locationId,
					payPlan: createdPayPlan,
				},
				{
					onSuccess: (response) => {
						// Sanitize special cases
						NEEDS_SANITIZATION.forEach((property) => {
							if (isPropertyIncluded(response, property)) {
								const isEmpty = !response?.[property]?.length;
								if (isEmpty) {
									response[property] = null;
								}
							}
						});
						saveNewCreatedPayPlan(currentId, response);
					},
					onError: (error) => {
						console.error(error);
					},
				}
			);
		},
		[location, initialModel, saveNewCreatedPayPlan]
	);

	useEffect(() => {
		const isNewInPlace = location && (isNew || initialModel?._id?.includes("new_"));
		const isInstantCreation = and(instantCreation, isNewInPlace);
		const isUpdatingNew = and(updatedSections, isNewInPlace);
		const isUpdating = and(updatedSections, !isNewInPlace);

		// Save a newly created pay plan
		if (or(isInstantCreation, isUpdatingNew) && !wasCreated.current) {
			wasCreated.current = true;
			const createdPayPlan = getNewPayPlanDefinition(initialModel, updatedSections, { location, user });
			handleSaveNewPayPlan(createdPayPlan);
			return;
		}

		// Update an existing pay plan
		if (and(isUpdating, !wasUpdated.current)) {
			wasUpdated.current = true;
			handleUpdatePayPlan(updatedSections);
			return;
		}

		return () => {};
	}, [location?.locationId, isNew, selected, initialModel, instantCreation, user?._id, updatedSections, handleSaveNewPayPlan]);

	useEffect(() => {
		setIsLoadingModel(or(isLoading, isLoadingModality, updating, saving));
	}, [isLoading, isLoadingModality, updating, saving, setIsLoadingModel]);

	useEffect(() => {
		setInitialModel(data);
	}, [data, setInitialModel]);

	useEffect(() => {
		setIsPayPlanLocked(data?.locked || false);
	}, [data, setIsPayPlanLocked]);

	useEffect(() => {
		setIsPayPlanAvailable(data?.available || false);
	}, [data, setIsPayPlanAvailable]);

	useEffect(() => {
		const editing = isEditingMode(data, user?._id);
		setIsEditing(editing);
	}, [data, user, setIsEditing]);

	useEffect(() => {
		queryClient.setQueryData(["payPlan", positionId, location?.locationId, selected?._id], (previous) => {
			const currentData = previous;
			if (currentData) {
				currentData.available = isPayPlanAvailable;
			}
			return currentData;
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isPayPlanAvailable]);

	useEffect(() => {
		if (!location) {
			return;
		}
	}, [location]);

	if (!selected) {
		const emptyTitle = !position ? emptySelectionTitle : `Select a ${position} Pay Plan`;
		return (
			<EmptyBody
				image={empty}
				title={emptyTitle}
				bodyHeightClassName={"!h-[510px]"}
				sx={{
					boxShadow: "none !important",
				}}
			/>
		);
	}

	return (
		<Fragment>
			{Boolean(isValidElement(children)) &&
				cloneElement(children, {
					data,
					drawModality,
					isNew,
					instantCreation,
					location,
					readOnly,
					fetching: isFetching,
					saving: or(saving, updating),
					loading: or(isLoading, isLoadingModality),
					updateModel: update,
				})}
		</Fragment>
	);
}
FactoryModel.propTypes = {
	children: PropTypes.node,
};
